/**
 * docJson (TipTap ContractRichEditor 저장 결과) 을 편집기와 동일한 DOM/CSS 로
 * 읽기 전용 렌더링. Step 2 필드 배치·Step 3 검토 미리보기의 배경 폴백 공용 모듈.
 *
 * 스타일은 CONTRACT_PAGE_CSS 를 <style> 로 주입해 .contract-page-canvas 스코프에 적용.
 */
import type { CSSProperties } from "react";

export const renderDocNode = (node: any, key: string): any => {
  if (!node) return null;
  const t = node.type;
  const content = Array.isArray(node.content) ? node.content : [];
  if (t === "text") {
    let el: any = node.text;
    const marks = Array.isArray(node.marks) ? node.marks : [];
    for (const m of marks) {
      if (m.type === "bold") el = <strong>{el}</strong>;
      if (m.type === "textStyle" && m.attrs) {
        const st: CSSProperties = {};
        if (m.attrs.fontSize) st.fontSize = m.attrs.fontSize;
        if (m.attrs.color) st.color = m.attrs.color;
        if (m.attrs.backgroundColor) st.backgroundColor = m.attrs.backgroundColor;
        if (m.attrs.fontFamily) st.fontFamily = m.attrs.fontFamily;
        if (m.attrs.lineHeight) st.lineHeight = m.attrs.lineHeight;
        if (m.attrs.letterSpacing) st.letterSpacing = m.attrs.letterSpacing;
        if (Object.keys(st).length) el = <span style={st}>{el}</span>;
      }
    }
    return <span key={key}>{el}</span>;
  }
  if (t === "contractVariable") {
    return (
      <span key={key} style={{
        background: "#FFF7E6", color: "#FF9934", fontWeight: 600,
        padding: "0 4px", borderRadius: 3,
      }}>{node.attrs?.label || node.attrs?.varKey || "?"}</span>
    );
  }
  if (t === "paragraph") {
    const align = node.attrs?.textAlign;
    const style: CSSProperties = {};
    if (align) style.textAlign = align as any;
    return (
      <p key={key} data-text-align={align || undefined} style={style}>
        {content.map((c: any, i: number) => renderDocNode(c, `${key}-${i}`))}
      </p>
    );
  }
  if (t === "table") {
    const firstRow = content[0];
    const cells = firstRow?.content ?? [];
    const cols: number[] = [];
    for (const c of cells) {
      const w = c?.attrs?.colwidth;
      if (Array.isArray(w)) cols.push(...w.map((n: any) => Number(n) || 0));
      else cols.push(0);
    }
    return (
      <div key={key} className="tableWrapper">
        <table className="contract-table">
          {cols.some((w) => w > 0) && (
            <colgroup>
              {cols.map((w, i) => (
                <col key={i} style={w > 0 ? { width: `${w}px` } : undefined} />
              ))}
            </colgroup>
          )}
          <tbody>
            {content.map((row: any, i: number) => renderDocNode(row, `${key}-r${i}`))}
          </tbody>
        </table>
      </div>
    );
  }
  if (t === "tableRow") {
    return <tr key={key}>{content.map((cell: any, i: number) => renderDocNode(cell, `${key}-c${i}`))}</tr>;
  }
  if (t === "tableCell" || t === "tableHeader") {
    const Tag = (t === "tableHeader" ? "th" : "td") as any;
    const a = node.attrs || {};
    return (
      <Tag key={key}
        colSpan={a.colspan && a.colspan > 1 ? a.colspan : undefined}
        rowSpan={a.rowspan && a.rowspan > 1 ? a.rowspan : undefined}
      >
        {content.map((c: any, i: number) => renderDocNode(c, `${key}-i${i}`))}
      </Tag>
    );
  }
  if (t === "positionedText") {
    const a = node.attrs || {};
    return (
      <div key={key} style={{
        position: "absolute", left: a.x, top: a.y,
        fontSize: `${a.fontSize || 11}pt`,
        color: a.color || "#111",
        fontWeight: a.bold ? 700 : 400,
      }}>{content.map((c: any, i: number) => renderDocNode(c, `${key}-${i}`))}</div>
    );
  }
  return null;
};

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

/** 특정 페이지 하나만 편집기(contractPage NodeView) 와 동일한 attr·style·class 로 렌더 */
export const renderPageBackground = (docJson: any, pageNo: number) => {
  if (!docJson?.content) return null;
  const pages = docJson.content.filter((n: any) => n?.type === "contractPage");
  const target = pages[pageNo - 1];
  if (!target?.content) return null;
  const width = target.attrs?.width || A4_WIDTH_PX;
  const height = target.attrs?.height || A4_HEIGHT_PX;
  // 편집기 contractPage NodeView(renderHTML) 와 동일한 DOM 형태
  return (
    <div
      data-contract-page="true"
      data-page-number={String(pageNo)}
      data-page-width={String(width)}
      className="contract-page-canvas"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        boxSizing: "border-box",
        width, minWidth: width, maxWidth: width,
        height, minHeight: height, maxHeight: height,
        overflow: "hidden",
        pointerEvents: "none",
        background: "#fff",
      }}
    >
      {target.content.map((n: any, i: number) => renderDocNode(n, `p${pageNo}-${i}`))}
    </div>
  );
};
